import React, { useMemo } from "react";
import { useAppSelector } from "../../store";
import { userActions } from "../../slices/userSlice";
import { localErrorsMessages } from "../../constants/errors";
import { InputFormElement } from "../../components/UIComponents/InputFormElement";
import { FormLayout } from "../../components/FormLayout/FormLayout";
import { AuthorizationCodeInput } from "../../components/AuthorizationCodeInput/AuthorizationCodeInput";
import { ValidationSchema } from "../../utils/useValidation";

const newLoginValidationConditions = [
  {
    labelTrKey: "changeUsername.validatorConditions.min5Max50CharsAndAllowedSigns",
    testFn: (phrase: string) =>
      phrase.length >= 5 && phrase.length <= 50 && !/[^a-zA-Z@.\-_0-9]/.test(phrase),
  },
  {
    labelTrKey: "changeUsername.validatorConditions.minOneLetter",
    testFn: (phrase: string) => /[a-zA-Z]/.test(phrase),
  },
];

export const ChangeLogin: React.FC = () => {
  const { errorTrKey, formData } = useAppSelector((state) => state.user);
  const initialState = {
    authorizationCode: "",
    newUsername: "",
    confirmedNewUsername: "",
  };
  const validationSchema: ValidationSchema<typeof initialState> = useMemo(
    () => ({
      authorizationCode: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey,
          validate: (value) =>
            errorTrKey !== localErrorsMessages["007"] || formData.authorizationCode !== value,
          triggerEvent: "change",
        },
      ],
      newUsername: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey,
          validate: (value) =>
            errorTrKey !== localErrorsMessages["012"] || formData.newUsername !== value,
          triggerEvent: "change",
        },
      ],
      confirmedNewUsername: [
        {
          errorTrKey: "validation.rules.required",
          validate: (value) => value !== "",
          triggerEvent: "blur",
        },
        {
          errorTrKey: "changeUsername.usernamesNotMatch",
          validate: (value, allValues) => value === "" || value === allValues.newUsername,
          triggerEvent: "change",
        },
      ],
    }),
    [errorTrKey, formData]
  );

  return (
    <FormLayout
      titleTrKey="changeUsername.title"
      submitTrKey="button.save"
      initialState={initialState}
      validationSchema={validationSchema}
      validator={{
        titleTrKey: "changeUsername.usernameConditionsTitle",
        phraseFieldId: "newUsername",
        conditions: newLoginValidationConditions,
      }}
      onSubmitAction={(fieldsValue) =>
        userActions.loginChangeConfirm({
          authorizationCode: fieldsValue.authorizationCode,
          newUsername: fieldsValue.newUsername,
          username: formData.username,
        })
      }
      leftTitleTrKey="registerForm.enterOneTimepassword"
      leftComponent={(props) => (
        <AuthorizationCodeInput
          {...props}
          onResendAction={(formData) =>
            userActions.loginChangeInit({ username: formData.username })
          }
        />
      )}
      rightTitleTrKey="changeUsername.setNewLogin"
      rightComponent={({
        fieldsValue,
        onFieldValueChange,
        onFieldValueBlur,
        hasValidationError,
        getValidationMessagesTrKeys,
      }) => (
        <>
          <InputFormElement
            labelProps={{
              labelTrKey: "changeUsername.newUsernamePlaceholder",
            }}
            inputProps={{
              value: fieldsValue.newUsername,
              onChange: onFieldValueChange("newUsername", "confirmedNewUsername"),
              onBlur: onFieldValueBlur("newUsername"),
              isInvalid: hasValidationError("newUsername"),
              validationMessagesTrKeys: getValidationMessagesTrKeys("newUsername"),
            }}
          />
          <InputFormElement
            labelProps={{
              labelTrKey: "changeUsername.repeatNewUsernamePlaceholder",
            }}
            inputProps={{
              value: fieldsValue.confirmedNewUsername,
              onChange: onFieldValueChange("confirmedNewUsername", "newUsername"),
              onBlur: onFieldValueBlur("confirmedNewUsername"),
              isInvalid: hasValidationError("confirmedNewUsername"),
              validationMessagesTrKeys: getValidationMessagesTrKeys("confirmedNewUsername"),
            }}
          />
        </>
      )}
    />
  );
};
